import { utcDay } from '../sim/rng';
import type { RankedMode } from '../sim/types';
import { store, type PendingScore } from '../store';

export class ApiError extends Error {
  constructor(message: string, readonly status: number) {
    super(message);
  }
}

/** Network failure (offline, timeout): worth retrying later. */
export const isNetworkError = (e: unknown) => !(e instanceof ApiError) || e.status >= 500;

async function request<T>(path: string, init: RequestInit = {}, timeoutMs = 6000): Promise<T> {
  const ctl = new AbortController();
  const timer = setTimeout(() => ctl.abort(), timeoutMs);
  try {
    const res = await fetch(path, {
      ...init,
      signal: ctl.signal,
      headers: init.body ? { 'content-type': 'application/json' } : undefined,
    });
    const data = (await res.json().catch(() => ({}))) as { error?: string };
    if (!res.ok) throw new ApiError(data.error ?? `Error ${res.status}`, res.status);
    return data as T;
  } finally {
    clearTimeout(timer);
  }
}

export interface RunTicket {
  runId: string;
  seed: number;
  token: string;
  mode: RankedMode;
  day: string;
  fetchedAt: number;
}

export interface SubmitResult {
  ok: true;
  scoreId: number;
  score: number;
  nickname: string;
  rank: number;
  total: number;
  rankDay: number;
}

export interface BoardEntry {
  id: number;
  rank: number;
  nickname: string;
  score: number;
  jumps: number;
  bestCombo: number;
  createdAt: number;
}

export interface Board {
  mode: RankedMode;
  period: 'day' | 'week' | 'all';
  day: string;
  total: number;
  cutoff: number | null;
  entries: BoardEntry[];
  me: BoardEntry | null;
}

export const api = {
  createRun(mode: RankedMode, timeoutMs = 5000): Promise<Omit<RunTicket, 'fetchedAt'>> {
    return request('/api/runs', { method: 'POST', body: JSON.stringify({ mode }) }, timeoutMs);
  },
  submitScore(body: Omit<PendingScore, 'savedAt' | 'mode'>): Promise<SubmitResult> {
    return request('/api/scores', { method: 'POST', body: JSON.stringify(body) }, 10000);
  },
  leaderboard(mode: RankedMode, period: 'day' | 'week' | 'all', me?: number, limit = 10): Promise<Board> {
    const q = new URLSearchParams({ mode, period, limit: String(limit) });
    if (me) q.set('me', String(me));
    return request(`/api/leaderboard?${q}`);
  },
};

const MAX_TICKET_AGE = 90 * 60 * 1000;

/** Keeps one ranked run ready per mode so a game can start instantly. */
export class RunPool {
  private ready: Partial<Record<RankedMode, RunTicket>> = {};
  private inflight: Partial<Record<RankedMode, Promise<RunTicket | null>>> = {};

  private fresh(t: RunTicket | undefined): t is RunTicket {
    if (!t) return false;
    if (Date.now() - t.fetchedAt > MAX_TICKET_AGE) return false;
    return t.mode !== 'daily' || t.day === utcDay(Date.now());
  }

  prefetch(mode: RankedMode): Promise<RunTicket | null> {
    if (this.fresh(this.ready[mode])) return Promise.resolve(this.ready[mode]!);
    const existing = this.inflight[mode];
    if (existing) return existing;
    const p = api
      .createRun(mode)
      .then((r) => {
        const t: RunTicket = { ...r, mode, fetchedAt: Date.now() };
        this.ready[mode] = t;
        return t;
      })
      .catch(() => null)
      .finally(() => {
        delete this.inflight[mode];
      });
    this.inflight[mode] = p;
    return p;
  }

  /** Takes a ready run, waiting up to `waitMs` for one. Null means play unranked. */
  async take(mode: RankedMode, waitMs: number): Promise<RunTicket | null> {
    let t = this.ready[mode];
    if (!this.fresh(t)) {
      t = (await Promise.race([this.prefetch(mode), new Promise<null>((r) => setTimeout(() => r(null), waitMs))])) ?? undefined;
    }
    if (!this.fresh(t)) return null;
    delete this.ready[mode];
    return t;
  }
}

/** Sends scores that could not be saved earlier because the device was offline. */
export async function flushPending(): Promise<void> {
  const list = store.pending();
  if (!list.length) return;
  const keep: PendingScore[] = [];
  for (const p of list) {
    try {
      const { mode, savedAt: _savedAt, ...body } = p;
      const r = await api.submitScore(body);
      store.setMyScore(mode, r.scoreId, r.score, utcDay(Date.now()));
    } catch (e) {
      if (isNetworkError(e)) keep.push(p);
    }
  }
  store.setPending(keep);
}
